import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useContent } from '../hooks/useContent';
import { useToast } from '../hooks/useToast';
import { useEditorPreview } from '../hooks/useEditorPreview';
import { getContentId } from '../types';
import { api } from '../api/client';
import { getCachedMediaUrl, cacheMediaUrls } from '../api/mediaCache';
import ImageUploader from '../components/ImageUploader';
import HelpSidebar from '../components/HelpSidebar';
import SectionToggle from '../components/SectionToggle';
import { pageHelp } from '../data/helpContent';
import '../styles/editor.css';

export default function GalleryEditor() {
  const { siteId } = useParams<{ siteId: string }>();
  const { items, loading, error, create, remove, reorder } = useContent(siteId, 'gallery');
  const { addToast } = useToast();
  useEditorPreview('/');
  const [editingCaption, setEditingCaption] = useState<string | null>(null);
  const [captionText, setCaptionText] = useState('');
  const [mediaUrls, setMediaUrls] = useState<Record<string, string>>({});

  // Resolve presigned URLs for gallery images
  useEffect(() => {
    if (!siteId || items.length === 0) return;
    // Collect all image keys that need resolving
    const keys = items
      .map((item) => (item.data as Record<string, string>).imageUrl)
      .filter((k): k is string => !!k && !k.startsWith('http'));

    // Check cache first
    const uncached = keys.filter((k) => !getCachedMediaUrl(k));
    if (uncached.length === 0) {
      // All cached — build the map
      const urlMap: Record<string, string> = {};
      for (const k of keys) {
        const url = getCachedMediaUrl(k);
        if (url) urlMap[k] = url;
      }
      setMediaUrls(urlMap);
      return;
    }

    // Fetch media list to populate cache
    api.listMedia(siteId).then((mediaItems) => {
      cacheMediaUrls(mediaItems.map((m) => ({ key: m.key, url: m.url })));
      const urlMap: Record<string, string> = {};
      for (const k of keys) {
        const url = getCachedMediaUrl(k);
        if (url) urlMap[k] = url;
      }
      setMediaUrls(urlMap);
    }).catch(() => {});
  }, [siteId, items]);

  const handleAddImage = async (imageKey: string) => {
    if (!siteId || !imageKey) return;
    try {
      await create({ imageUrl: imageKey, caption: '' }, items.length);
      addToast('success', 'Image added to gallery');
    } catch (err) {
      addToast('error', err instanceof Error ? err.message : 'Failed to add');
    }
  };

  const handleDelete = async (contentKey: string) => {
    try {
      await remove(getContentId(contentKey));
      addToast('success', 'Gallery item removed');
    } catch (err) {
      addToast('error', err instanceof Error ? err.message : 'Failed to delete');
    }
  };

  const handleMoveUp = async (index: number) => {
    if (index === 0) return;
    const newItems = [...items];
    [newItems[index - 1], newItems[index]] = [newItems[index], newItems[index - 1]];
    const ordered = newItems.map((item, i) => ({
      id: getContentId(item.contentKey),
      sortOrder: i,
    }));
    try {
      await reorder(ordered);
    } catch (err) {
      addToast('error', err instanceof Error ? err.message : 'Failed to reorder');
    }
  };

  const handleMoveDown = async (index: number) => {
    if (index >= items.length - 1) return;
    const newItems = [...items];
    [newItems[index], newItems[index + 1]] = [newItems[index + 1], newItems[index]];
    const ordered = newItems.map((item, i) => ({
      id: getContentId(item.contentKey),
      sortOrder: i,
    }));
    try {
      await reorder(ordered);
    } catch (err) {
      addToast('error', err instanceof Error ? err.message : 'Failed to reorder');
    }
  };

  if (loading) {
    return (
      <div className="page">
        <div className="page-header"><h1>Gallery</h1></div>
        <div className="loading-state"><div className="spinner" /></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="page">
        <div className="page-header"><h1>Gallery</h1></div>
        <div className="error-state"><p>{error}</p></div>
      </div>
    );
  }

  const help = pageHelp.gallery;

  return (
    <div className="page">
      <div className="page-header">
        <h1>Gallery</h1>
        <div className="page-header-actions">
          <SectionToggle siteId={siteId!} field="showGallery" label="Gallery" />
        </div>
      </div>

      <div className="editor-layout">
        <div className="editor-main">
          {items.length === 0 ? (
            <div className="empty-state">
              <div className="empty-icon"><svg width={48} height={48} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" style={{ color: '#ccc' }}><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><path d="M21 15l-5-5L5 21"/></svg></div>
              <h2>No gallery images yet</h2>
              <p>Show off your best work with a photo gallery.</p>
              <p style={{ fontSize: '0.85rem', color: 'var(--color-gray-500)', marginBottom: 16 }}>
                Images display in a grid layout on your site.
              </p>
              <div className="empty-state-preview-gallery">
                <div className="empty-state-preview-square" />
                <div className="empty-state-preview-square" />
                <div className="empty-state-preview-square" />
                <div className="empty-state-preview-square" />
                <div className="empty-state-preview-square" />
                <div className="empty-state-preview-square" />
              </div>
              <div className="gallery-item gallery-item-add" style={{ maxWidth: 240, margin: '0 auto' }}>
                <ImageUploader
                  siteId={siteId!}
                  value=""
                  onChange={handleAddImage}
                  label="+ Add your first image"
                />
              </div>
            </div>
          ) : (
          <div className="gallery-grid">
        {items.map((item, index) => {
          const data = item.data as Record<string, string>;
          const contentId = getContentId(item.contentKey);
          return (
            <div key={item.contentKey} className="gallery-item">
              <div className="gallery-item-image">
                {data.imageUrl && mediaUrls[data.imageUrl] ? (
                  <img
                    src={mediaUrls[data.imageUrl]}
                    alt={data.caption || 'Gallery image'}
                    onError={(e) => {
                      (e.target as HTMLImageElement).style.display = 'none';
                    }}
                  />
                ) : (
                  <div className="gallery-placeholder">No image</div>
                )}
              </div>
              <div className="gallery-item-footer">
                {editingCaption === contentId ? (
                  <div className="inline-edit">
                    <input
                      type="text"
                      value={captionText}
                      onChange={(e) => setCaptionText(e.target.value)}
                      className="field-input field-input-sm"
                      autoFocus
                    />
                    <button
                      className="btn-icon"
                      onClick={async () => {
                        try {
                          await api.updateContent(siteId!, 'gallery', contentId, {
                            ...data,
                            caption: captionText,
                          });
                          setEditingCaption(null);
                          addToast('success', 'Caption updated');
                        } catch (err) {
                          addToast('error', err instanceof Error ? err.message : 'Failed to update');
                        }
                      }}
                    >
                      ✓
                    </button>
                    <button className="btn-icon" onClick={() => setEditingCaption(null)}>
                      ✕
                    </button>
                  </div>
                ) : (
                  <span
                    className="gallery-caption"
                    onClick={() => {
                      setEditingCaption(contentId);
                      setCaptionText(data.caption || '');
                    }}
                    title="Click to edit caption"
                  >
                    {data.caption || 'Add caption…'}
                  </span>
                )}
                <div className="gallery-item-actions">
                  <button
                    className="btn-icon"
                    onClick={() => handleMoveUp(index)}
                    disabled={index === 0}
                    title="Move left"
                  >
                    ←
                  </button>
                  <button
                    className="btn-icon"
                    onClick={() => handleMoveDown(index)}
                    disabled={index === items.length - 1}
                    title="Move right"
                  >
                    →
                  </button>
                  <button
                    className="btn-icon btn-icon-danger"
                    onClick={() => handleDelete(item.contentKey)}
                    title="Delete"
                  >
                    ✕
                  </button>
                </div>
              </div>
            </div>
          );
        })}

        {/* Add new gallery item */}
        <div className="gallery-item gallery-item-add">
          <ImageUploader
            siteId={siteId!}
            value=""
            onChange={handleAddImage}
            label="Add to gallery"
          />
        </div>
          </div>
          )}
        </div>
        <HelpSidebar title={help.title} description={help.description} tips={help.tips} />
      </div>
    </div>
  );
}
